import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Clock, MapPin, Share2, CalendarPlus, CheckCircle2,
} from 'lucide-react';
import { Reveal } from '../components/ds/theme';
import { EVENTS_FEED_URL, RSVP_URL, decorateEvent } from '../lib/eventsMeta';

// ── Google Calendar link builder ─────────────────────────────────────────────
// Builds a "Add to Google Calendar" URL from the event's stored start /
// end date + string times. Times like "11:00 AM" get parsed into 24-hour
// hours + minutes and applied to the start/end dates. Missing endTime
// defaults to start + 1 hour so the calendar entry isn't zero-length.
function parseTimeStr(t) {
  if (!t) return null;
  const m = /^\s*(\d{1,2}):(\d{2})\s*(AM|PM)?\s*$/i.exec(t);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ampm = (m[3] || '').toUpperCase();
  if (ampm === 'PM' && h < 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return { h, min };
}

function toIcsUtc(date, timeStr) {
  const d = new Date(date);
  const t = parseTimeStr(timeStr);
  if (t) { d.setHours(t.h, t.min, 0, 0); }
  // Format YYYYMMDDTHHmmssZ (UTC)
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
}

function googleCalendarUrl(event) {
  if (!event.startDate) return null;
  const startTimeStr = event.startTime || '11:00 AM';
  const startIso = toIcsUtc(event.startDate, startTimeStr);
  // End: use event.endDate + endTime if present; else start + 1h
  let endIso;
  if (event.endDate) {
    endIso = toIcsUtc(event.endDate, event.endTime || event.startTime || '12:00 PM');
  } else {
    const startD = new Date(event.startDate);
    const t = parseTimeStr(startTimeStr) || { h: 11, min: 0 };
    startD.setHours(t.h + 1, t.min, 0, 0);
    endIso = toIcsUtc(startD.toISOString(), null);
  }
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${startIso}/${endIso}`,
    details: event.description || '',
    location: event.location || '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// ── Page ─────────────────────────────────────────────────────────────────────

function EventDetail() {
  const { id } = useParams();
  const location = useLocation();

  // Fast path: card click passed the event via location.state so we render
  // instantly. Direct URL load (email link) falls back to fetching the feed.
  const passed = location.state?.event;
  const [event, setEvent] = useState(passed ? decorateEvent(passed) : null);
  const [loading, setLoading] = useState(!passed);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (passed) return;
    fetch(EVENTS_FEED_URL)
      .then((r) => r.json())
      .then((data) => {
        const raw = (data.events || []).find((e) => e.id === id);
        if (!raw) { setNotFound(true); setLoading(false); return; }
        setEvent(decorateEvent(raw));
        setLoading(false);
      })
      .catch(() => { setLoading(false); setNotFound(true); });
  }, [id, passed]);

  useEffect(() => {
    if (event?.title) document.title = `${event.title} · Bright Light Baptist Church`;
    return () => { document.title = 'Bright Light Baptist Church'; };
  }, [event]);

  if (loading) {
    return (
      <main style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>Loading event…</p>
      </main>
    );
  }
  if (notFound || !event) {
    return (
      <main style={{ maxWidth: '720px', margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
        <h1 className="v2-serif" style={{ fontSize: '32px', color: 'var(--navy-800)', margin: '0 0 12px' }}>Event not found</h1>
        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>This event may have been removed or the link is out of date.</p>
        <Link to="/events" style={{ color: 'var(--burgundy-700)', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <ArrowLeft size={16} /> Back to Events
        </Link>
      </main>
    );
  }

  return (
    <main>
      <EventHero event={event} />
      <section style={{ background: 'var(--white)', padding: '48px var(--gutter) 80px' }}>
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>
          <Link to="/events" style={{ color: 'var(--burgundy-700)', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', marginBottom: '28px' }}>
            <ArrowLeft size={16} /> All events
          </Link>
          <Reveal>
            <MetadataBlock event={event} />
          </Reveal>
          <Reveal>
            <ActionRow event={event} />
          </Reveal>
          {event.description && (
            <Reveal>
              <div style={{ marginTop: '36px' }}>
                <h2 className="v2-serif" style={{ fontSize: '22px', color: 'var(--navy-800)', margin: '0 0 12px', fontWeight: 500 }}>About this event</h2>
                <p style={{ fontFamily: 'var(--font-ui)', fontSize: '16px', color: 'var(--text-body)', lineHeight: 1.75, margin: 0, whiteSpace: 'pre-wrap' }}>{event.description}</p>
              </div>
            </Reveal>
          )}
          {event.rsvpMode && event.rsvpMode !== 'off' && (
            <Reveal>
              <div style={{ marginTop: '36px' }}>
                <RsvpBlock event={event} />
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </main>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────

function EventHero({ event }) {
  return (
    <div style={{
      position: 'relative',
      height: '360px',
      background: `linear-gradient(180deg, rgba(30,10,15,0.05) 0%, rgba(30,10,15,0.55) 100%), center/cover no-repeat url(${event.imageUrl})`,
      display: 'flex',
      alignItems: 'flex-end',
      padding: '0 var(--gutter) 40px',
    }}>
      <div style={{ maxWidth: '820px', margin: '0 auto', width: '100%' }}>
        <h1 className="v2-serif" style={{ color: 'var(--white)', fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 500, margin: 0, textShadow: '0 2px 20px rgba(0,0,0,0.35)', lineHeight: 1.15 }}>
          {event.title}
        </h1>
      </div>
    </div>
  );
}

// ── Metadata block ──────────────────────────────────────────────────────────

function MetadataBlock({ event }) {
  const rows = [
    { Icon: Calendar, label: event.dateLabel },
    event.timeLabel && { Icon: Clock, label: event.timeLabel },
    event.location && { Icon: MapPin, label: event.location },
  ].filter(Boolean);
  return (
    <div style={{ display: 'grid', gap: '12px', padding: '20px 22px', background: 'var(--tan-50, #faf6ef)', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)' }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontFamily: 'var(--font-ui)', fontSize: '15.5px', color: 'var(--text-body)' }}>
          <span style={{ color: 'var(--burgundy-700)', display: 'inline-flex' }}><r.Icon size={18} /></span>
          <span>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Action row ──────────────────────────────────────────────────────────────

function ActionRow({ event }) {
  const [copied, setCopied] = useState(false);
  const gcalUrl = useMemo(() => googleCalendarUrl(event), [event]);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const doShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: event.title, url: shareUrl }); return; } catch { /* fell through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  return (
    <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
      {gcalUrl && (
        <a href={gcalUrl} target="_blank" rel="noopener noreferrer" style={btnSecondary}>
          <CalendarPlus size={16} /> Add to Google Calendar
        </a>
      )}
      <button type="button" onClick={doShare} style={btnSecondary}>
        {copied ? <><CheckCircle2 size={16} /> Link copied!</> : <><Share2 size={16} /> Share</>}
      </button>
    </div>
  );
}

const btnSecondary = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px',
  fontFamily: 'var(--font-ui)',
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--burgundy-700)',
  background: 'var(--white)',
  border: '1.5px solid var(--burgundy-700)',
  borderRadius: 'var(--radius-md)',
  textDecoration: 'none',
  cursor: 'pointer',
};

// ── RSVP block ──────────────────────────────────────────────────────────────

function RsvpBlock({ event }) {
  const localKey = `rsvp:${event.id}`;
  const [alreadyRsvpd, setAlreadyRsvpd] = useState(false);

  // Detect Stripe redirect return: ?rsvp=success sets the local flag
  // so the visitor sees "You're going!" immediately (the webhook flips
  // paid=true on the RSVP doc asynchronously — this UI state is client-
  // only and doesn't wait on the roster to reflect it).
  useEffect(() => {
    try {
      if (localStorage.getItem(localKey) === '1') { setAlreadyRsvpd(true); return; }
    } catch { /* private mode */ }
    const params = new URLSearchParams(window.location.search);
    if (params.get('rsvp') === 'success') {
      setAlreadyRsvpd(true);
      try { localStorage.setItem(localKey, '1'); } catch { /* noop */ }
    }
  }, [localKey]);

  const [count, setCount] = useState(event.rsvpCount || 0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Payment intent from the event config.
  const paymentMode = event.paymentMode || 'off'; // 'off' | 'suggested' | 'required'
  const paymentAmountCents = event.paymentAmountCents || 0;
  const paymentLabel = event.paymentLabel || '';
  const hasSuggested = paymentMode === 'suggested' && paymentAmountCents > 0;
  const hasRequired = paymentMode === 'required' && paymentAmountCents > 0;
  const [includeDonation, setIncludeDonation] = useState(true); // suggested = pre-checked

  const persistRsvpd = () => {
    setAlreadyRsvpd(true);
    try { localStorage.setItem(localKey, '1'); } catch { /* noop */ }
  };

  const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;

  const submit = async () => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const paying = hasRequired || (hasSuggested && includeDonation);
      const body = event.rsvpMode === 'anonymous'
        ? { eventId: event.id, mode: 'anonymous' }
        : {
            eventId: event.id, mode: 'named',
            name: name.trim(), email: email.trim(),
            ...(paying ? {
              paymentAmountCents,
              returnUrl: window.location.origin + window.location.pathname,
            } : {}),
          };
      const res = await fetch(RSVP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'RSVP failed');
      // Paid path — Stripe returned a Checkout URL, redirect there.
      if (data.checkoutUrl) {
        window.location.assign(data.checkoutUrl);
        return;
      }
      if (event.rsvpMode === 'anonymous') setCount((c) => c + 1);
      else if (data.first) setCount((c) => c + 1);
      persistRsvpd();
    } catch (e) {
      setErrorMsg(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmed = alreadyRsvpd;
  const isNamed = event.rsvpMode === 'named';
  const canSubmit = !submitting && (!isNamed || (name.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)));

  // Button label reflects whether the next click charges a card.
  const willPay = hasRequired || (hasSuggested && includeDonation);
  let submitLabel;
  if (submitting) submitLabel = willPay ? 'Redirecting to payment…' : 'Sending…';
  else if (!isNamed) submitLabel = "I'll be there";
  else if (willPay) submitLabel = `Continue to Payment — ${dollars(paymentAmountCents)}`;
  else submitLabel = 'RSVP';

  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const cancelled = params?.get('rsvp') === 'cancelled';

  return (
    <div style={{ padding: '24px', border: `2px solid ${confirmed ? 'var(--success, #2f7d43)' : 'var(--burgundy-700)'}`, borderRadius: 'var(--radius-md)', background: confirmed ? 'rgba(47,125,67,0.06)' : 'var(--burgundy-50, #faf4f5)' }}>
      <h2 className="v2-serif" style={{ fontSize: '22px', color: 'var(--navy-800)', margin: '0 0 4px', fontWeight: 500 }}>
        {confirmed ? "You're going!" : 'RSVP for this event'}
      </h2>
      <p style={{ fontFamily: 'var(--font-ui)', fontSize: '14px', color: 'var(--text-muted)', margin: '0 0 18px' }}>
        {count > 0 && `${count} ${count === 1 ? 'person' : 'people'} going`}
        {count === 0 && !confirmed && "Be the first to RSVP."}
      </p>

      {cancelled && !confirmed && (
        <div style={{ marginBottom: '14px', padding: '10px 12px', background: 'rgba(177,50,50,0.08)', border: '1px solid rgba(177,50,50,0.25)', borderRadius: 'var(--radius-md)', color: 'var(--error, #b13232)', fontSize: '14px', fontFamily: 'var(--font-ui)' }}>
          Payment was cancelled. Your RSVP wasn't recorded — try again below.
        </div>
      )}

      {!confirmed && isNamed && (
        <div style={{ display: 'grid', gap: '10px', marginBottom: '14px' }}>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" style={inputStyle} autoComplete="name" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" style={inputStyle} autoComplete="email" />
        </div>
      )}

      {!confirmed && hasRequired && (
        <div style={{ marginBottom: '14px', padding: '12px 14px', background: 'rgba(139,115,85,0.08)', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-ui)', fontSize: '14px', color: 'var(--text-body)' }}>
          <strong>{paymentLabel || 'Payment'}: {dollars(paymentAmountCents)}</strong>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Required — you'll be sent to a secure Stripe checkout page.
          </div>
        </div>
      )}

      {!confirmed && hasSuggested && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '14px', padding: '12px 14px', background: 'rgba(139,115,85,0.08)', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-ui)', fontSize: '14px', color: 'var(--text-body)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeDonation}
            onChange={(e) => setIncludeDonation(e.target.checked)}
            style={{ marginTop: '2px', accentColor: 'var(--burgundy-700)', width: '18px', height: '18px' }}
          />
          <span>
            <strong>Include {dollars(paymentAmountCents)} {paymentLabel || 'contribution'}</strong>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Optional — uncheck to RSVP without a payment.
            </div>
          </span>
        </label>
      )}

      {!confirmed && (
        <button type="button" disabled={!canSubmit} onClick={submit} style={{
          ...btnSecondary,
          padding: '12px 22px',
          fontSize: '15px',
          background: canSubmit ? 'var(--burgundy-700)' : 'var(--tan-300, #d9c9b4)',
          color: 'var(--white)',
          borderColor: canSubmit ? 'var(--burgundy-700)' : 'transparent',
          cursor: canSubmit ? 'pointer' : 'not-allowed',
        }}>
          {submitLabel}
        </button>
      )}

      {confirmed && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--success, #2f7d43)', fontWeight: 600, fontFamily: 'var(--font-ui)' }}>
          <CheckCircle2 size={20} /> Thanks — see you there.
        </div>
      )}

      {errorMsg && (
        <p style={{ marginTop: '10px', color: 'var(--error, #b13232)', fontSize: '13px', fontFamily: 'var(--font-ui)' }}>{errorMsg}</p>
      )}
    </div>
  );
}

const inputStyle = {
  padding: '12px 14px',
  fontSize: '15px',
  fontFamily: 'var(--font-ui)',
  border: '1.5px solid var(--line)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--white)',
  color: 'var(--text-body)',
  outline: 'none',
};

export default EventDetail;
